import axios, {AxiosRequestConfig, AxiosResponse} from 'axios';
import qs from 'qs';
import config from '../config/config';
import fileDownload from 'js-file-download';
import {
    APourRole,
    AppVersion,
    EntrepriseCorpsEtat,
    Lot, Message,
    Operation,
    OperationElementAnnexe,
    Photo,
    Reserve,
    User
} from "../common/types/Types";
import {CommonHelper} from "../common/CommonHelper";

/* APIs ENDPOINTS */
const SESSION_STORAGE_SESSION = 'session';
const SERVER_URL = config.backend.url;
const LOGIN_URL = SERVER_URL + '/auth/login';
const FORGOT_PASSWORD_URL = SERVER_URL + '/auth/forgot_password';
const ACTIVATE_FORGOT_PASSWORD_TOKEN_URL = SERVER_URL + '/auth/token';
const CURRENT_USER_URL = SERVER_URL + '/auth/profile';
const USERS_URL = SERVER_URL + '/users';
const USERS_INDEX_URL = SERVER_URL + '/users/{id}';
const OPERATION_URL = SERVER_URL + '/operations';
const OPERATION_INDEX_URL = SERVER_URL + '/operations/{id}';
const OPERATION_LOTS_INDEX_URL = SERVER_URL + '/operations/{id}/lots';
const OPERATION_LOTS_ADDITIONAL_ELEMENTS_INDEX_URL = SERVER_URL + '/operations/{id}/lots/missing-elements';
const OPERATION_RESERVES_INDEX_URL = SERVER_URL + '/operations/{id}/reserves';
const OPERATION_ELEMENTS_INDEX_URL = SERVER_URL + '/operations/{id}/elements';
const OPERATION_ENTERPRISES_INDEX_URL = SERVER_URL + '/operations/{id}/enterprises';
const OPERATION_USERS_INDEX_URL = SERVER_URL + '/operations/{id}/users';
const OPERATION_AVAILABLE_USERS_INDEX_URL = SERVER_URL + '/operations/{id}/users/available';
const OPERATION_ADD_USER_URL = SERVER_URL + '/operations/{id}/users/{userId}/roles/{roleId}';
const OPERATION_REMOVE_USER_URL = SERVER_URL + '/operations/{id}/users/{refId}';
const LOT_URL = SERVER_URL + '/lots';
const LOT_INDEX_URL = SERVER_URL + '/lots/{id}';
const LOT_PDF_INDEX_URL = SERVER_URL + '/lots/pdf';
const LOT_CSV_INDEX_URL = SERVER_URL + '/lots/csv';
const LOT_RESERVES_INDEX_URL = SERVER_URL + '/lots/{id}/reserves';
const RESERVE_URL = SERVER_URL + '/reserves';
const RESERVE_INDEX_URL = SERVER_URL + '/reserves/{id}';
const RESERVE_PDF_INDEX_URL = SERVER_URL + '/reserves/pdf';
const RESERVE_CSV_INDEX_URL = SERVER_URL + '/reserves/csv';
const RESERVE_STATUS_INDEX_URL = SERVER_URL + '/reserves/{id}/status/{idStatus}';
const RESERVE_PHOTO_INDEX_URL = SERVER_URL + '/reserves/{id}/photos';
const RESERVE_DOC_LEVEE_INDEX_URL = SERVER_URL + '/reserves/{id}/doc/levee';
const APP_VERSION_URL = SERVER_URL + '/app-version';
const LAST_APP_VERSION_INDEX_URL = SERVER_URL + '/app-version/latest';
const APP_VERSION_INDEX_URL = SERVER_URL + '/app-version/{id}';
const MESSAGE_URL = SERVER_URL + '/message';
const LAST_MESSAGE_INDEX_URL = SERVER_URL + '/message/latest';
const MESSAGE_INDEX_URL = SERVER_URL + '/message/{id}';
const PHASE_URL = SERVER_URL + '/data/phases';
const LOCALISATION_URL = SERVER_URL + '/data/localisations';
const SITUATION_URL = SERVER_URL + '/data/situations';
const STATUS_URL = SERVER_URL + '/data/status';
const PARAMETER_URL = SERVER_URL + '/data/parameters';
const ROLE_URL = SERVER_URL + '/data/roles';
const ADDITIONAL_ELEMENT_URL = SERVER_URL + '/data/additional-elements';
const API_DOC_URL = SERVER_URL + '/api-docs';
const STATIC_URL = SERVER_URL + '/static';

export default class ApiHelper {

    static isTokenExpired(token) {
        const expiry = (JSON.parse(atob(token.split('.')[1]))).exp;

        return (Math.floor((new Date()).getTime() / 1000)) >= expiry;
    }

    static async isAuthenticated() {
        const token = localStorage.getItem(SESSION_STORAGE_SESSION);

        if (token === null) {
            return null;
        }

        if (ApiHelper.isTokenExpired(token)) {
            return null;
        }

        try {
            return (await ApiHelper.getUserDetail()).data;
        } catch (e) {
            ApiHelper.logout()
            return null;
        }
    }

    static buildHeaders(customHeaders?: {}, customOptions?: AxiosRequestConfig) {
        if (localStorage.getItem(SESSION_STORAGE_SESSION)) {
            return {
                headers: {
                    ...customHeaders,
                    'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem(SESSION_STORAGE_SESSION))
                },
                ...customOptions
            };
        } else {
            return null;
        }
    }

    static saveAuthentication(value) {
        localStorage.setItem(SESSION_STORAGE_SESSION, value)
    }

    static getToken() {
        return JSON.parse(localStorage.getItem(SESSION_STORAGE_SESSION));
    }

    static login(payload) {
        return axios.post(LOGIN_URL, {
            email: payload.email,
            username: payload.username,
            password: payload.password,
            mfaToken: payload.mfaToken,
        });
    }

    static logout() {
        localStorage.removeItem(SESSION_STORAGE_SESSION);
    }

    static getUserDetail() {
        return axios.get(CURRENT_USER_URL, ApiHelper.buildHeaders());
    }

    static getApiDocs() {
        return axios.get(API_DOC_URL, ApiHelper.buildHeaders());
    }

    static resetPassword(payload) {
        return axios.post(FORGOT_PASSWORD_URL, payload);
    }

    static activateForgotPasswordToken(payload) {
        return axios.post(ACTIVATE_FORGOT_PASSWORD_TOKEN_URL, {
            token: payload.token,
        });
    }

    static getUsers(parameters?: any) {
    const params = { ...parameters };

    if (typeof params.indicActif === "boolean") {
        params.indicActif = params.indicActif ? "true" : "false";
    }

    return axios.get(
        `${USERS_URL}?${qs.stringify(params, { skipNulls: true })}`,
        ApiHelper.buildHeaders()
    );
    }

    static getUsersCount() {
        return axios.get(USERS_URL + "/count", ApiHelper.buildHeaders());
    }

    static getCurrentUser(): Promise<AxiosResponse<User>> {
        return axios.get(CURRENT_USER_URL, ApiHelper.buildHeaders());
    }

    static getUser(userId): Promise<AxiosResponse<User>> {
        return axios.get(USERS_INDEX_URL.replace('{id}', userId), ApiHelper.buildHeaders());
    }

    static postUser(payload) {
        return axios.post(USERS_URL, payload, ApiHelper.buildHeaders());
    }

    static patchUser(userId, payload) {
        return axios.post(USERS_INDEX_URL.replace('{id}', userId), payload, ApiHelper.buildHeaders());
    }

    static getAppVersions(parameters?: any) {
        return axios.get(`${APP_VERSION_URL}?${qs.stringify(parameters, {skipNulls: true})}`, ApiHelper.buildHeaders());
    }

    static getLastAppVersion(): Promise<AxiosResponse<AppVersion>> {
        return axios.get(LAST_APP_VERSION_INDEX_URL, ApiHelper.buildHeaders());
    }

    static getAppVersion(userId): Promise<AxiosResponse<AppVersion>> {
        return axios.get(APP_VERSION_INDEX_URL.replace('{id}', userId), ApiHelper.buildHeaders());
    }

    static postAppVersion(formData: FormData) {
        return axios.post(APP_VERSION_URL, formData, ApiHelper.buildHeaders({
            'Content-Type': 'multipart/form-data'
        }));
    }

    static patchAppVersion(userId, formData) {
        return axios.post(APP_VERSION_INDEX_URL.replace('{id}', userId), formData, ApiHelper.buildHeaders({
            'Content-Type': 'multipart/form-data'
        }));
    }

    static getMessages(parameters?: any) {
        return axios.get(`${MESSAGE_URL}?${qs.stringify(parameters, {skipNulls: true})}`, ApiHelper.buildHeaders());
    }

    static getLastMessage(): Promise<AxiosResponse<Message>> {
        return axios.get(LAST_MESSAGE_INDEX_URL, ApiHelper.buildHeaders());
    }

    static getMessage(userId): Promise<AxiosResponse<Message>> {
        return axios.get(MESSAGE_INDEX_URL.replace('{id}', userId), ApiHelper.buildHeaders());
    }

    static postMessage(payload: Partial<Message>) {
        return axios.post(MESSAGE_URL, payload, ApiHelper.buildHeaders());
    }

    static patchMessage(userId, payload: Partial<Message>) {
        return axios.post(MESSAGE_INDEX_URL.replace('{id}', userId), payload, ApiHelper.buildHeaders());
    }

    static getRoles(parameters?) {
        return axios.get(`${ROLE_URL}?${qs.stringify(parameters, {skipNulls: true})}`, ApiHelper.buildHeaders());
    }

    static getAdditionalElements(parameters?) {
        return axios.get(`${ADDITIONAL_ELEMENT_URL}?${qs.stringify(parameters, {skipNulls: true})}`, ApiHelper.buildHeaders());
    }

    static getOperation(operationId): Promise<AxiosResponse<Operation>> {
        return axios.get(OPERATION_INDEX_URL.replace('{id}', operationId), ApiHelper.buildHeaders());
    }

    static getOperationLots(operationId): Promise<AxiosResponse<Lot[]>> {
        return axios.get(OPERATION_LOTS_INDEX_URL.replace('{id}', operationId), ApiHelper.buildHeaders());
    }

    static updateOperationLotsMissingAdditionalElements(operationId): Promise<AxiosResponse<{count: number;}>> {
        return axios.post(OPERATION_LOTS_ADDITIONAL_ELEMENTS_INDEX_URL.replace('{id}', operationId), {}, ApiHelper.buildHeaders());
    }

    static getOperationReserves(operationId): Promise<AxiosResponse<Reserve[]>> {
        return axios.get(OPERATION_RESERVES_INDEX_URL.replace('{id}', operationId), ApiHelper.buildHeaders());
    }

    static getOperationElements(operationId): Promise<AxiosResponse<OperationElementAnnexe[]>> {
        return axios.get(OPERATION_ELEMENTS_INDEX_URL.replace('{id}', operationId), ApiHelper.buildHeaders());
    }

    static postOperationElements(operationId, payload): Promise<AxiosResponse<OperationElementAnnexe[]>> {
        return axios.post(OPERATION_ELEMENTS_INDEX_URL.replace('{id}', operationId), payload, ApiHelper.buildHeaders());
    }

    static getOperationEnterprises(operationId): Promise<AxiosResponse<EntrepriseCorpsEtat[]>> {
        return axios.get(OPERATION_ENTERPRISES_INDEX_URL.replace('{id}', operationId), ApiHelper.buildHeaders());
    }

    static getOperationUsers(operationId): Promise<AxiosResponse<User[]>> {
        return axios.get(OPERATION_USERS_INDEX_URL.replace('{id}', operationId), ApiHelper.buildHeaders());
    }

    static getOperationAvailableUsers(operationId): Promise<AxiosResponse<User[]>> {
        return axios.get(OPERATION_AVAILABLE_USERS_INDEX_URL.replace('{id}', operationId), ApiHelper.buildHeaders());
    }

    static addUserToOperation(operationId, userId, roleId): Promise<AxiosResponse<APourRole>> {
        return axios.post(
            OPERATION_ADD_USER_URL
                .replace('{id}', operationId)
                .replace('{userId}', userId)
                .replace('{roleId}', roleId),
            {},
            ApiHelper.buildHeaders());
    }

    static removeUserFromOperation(operationId, aPourRoleId): Promise<AxiosResponse<void>> {
        return axios.delete(
            OPERATION_REMOVE_USER_URL
                .replace('{id}', operationId)
                .replace('{refId}', aPourRoleId),
            ApiHelper.buildHeaders());
    }

    static getOperations(parameters?) {
        return axios.get(`${OPERATION_URL}?${qs.stringify(parameters, {skipNulls: true})}`, ApiHelper.buildHeaders());
    }

    static postOperation(payload) {
        return axios.post(OPERATION_URL, payload, ApiHelper.buildHeaders());
    }

    static patchOperation(operationId, payload) {
        return axios.patch(OPERATION_INDEX_URL.replace('{id}', operationId), payload, ApiHelper.buildHeaders());
    }

    static getLot(lotId, isCleTransverse) {
        return axios.get(`${LOT_INDEX_URL.replace('{id}', lotId)}${isCleTransverse ? '?ct=1' : ''}`, ApiHelper.buildHeaders());
    }

    static getLotReserves(lotId): Promise<AxiosResponse<Reserve[]>> {
        return axios.get(LOT_RESERVES_INDEX_URL.replace('{id}', lotId), ApiHelper.buildHeaders());
    }

    static getLots(parameters?) {
        return axios.get(`${LOT_URL}?${qs.stringify(parameters, {skipNulls: true})}`, ApiHelper.buildHeaders());
    }

    static postLot(payload) {
        return axios.post(LOT_URL, payload, ApiHelper.buildHeaders());
    }

    static patchLot(lotId, payload) {
        return axios.post(LOT_INDEX_URL.replace('{id}', lotId), payload, ApiHelper.buildHeaders());
    }

    static getReserve(reserveId): Promise<AxiosResponse<Reserve>> {
        return axios.get(RESERVE_INDEX_URL.replace('{id}', reserveId), ApiHelper.buildHeaders());
    }

    static getReservesPdf(reserveIds: string[], parameters: any = {}): Promise<void> {
        return axios.post(RESERVE_PDF_INDEX_URL,
            {
                ids: reserveIds,
                parameters
            },
            ApiHelper.buildHeaders({}, {responseType: 'blob'}))
            .then((response) => {
                const currentDate = CommonHelper.formatDate(new Date(), 'yyyyMMdd_HHmmss');

                const fileName = parameters.exportByEnterprise ? `Liste Globale ${currentDate}.zip` : `Liste Globale ${currentDate}.pdf`;

                fileDownload(response.data, fileName);
            });
    }

    static getLotsPdf(lotIds: number[], parameters: any = {}): Promise<void> {
        return axios.post(LOT_PDF_INDEX_URL,
            {
                ids: lotIds,
                parameters
            },
            ApiHelper.buildHeaders({}, {responseType: 'blob'}))
            .then((response) => {
                const currentDate = CommonHelper.formatDate(new Date(), 'yyyyMMdd_HHmmss');

                const fileName = parameters.exportByEnterprise ? `Liste Globale ${currentDate}.zip` : `Liste Globale ${currentDate}.pdf`;
                fileDownload(response.data, fileName);
            });
    }

    static getReservesCsv(reserveIds: string[]): Promise<void> {
        return axios.post(RESERVE_CSV_INDEX_URL,
            {
                ids: reserveIds,
            },
            ApiHelper.buildHeaders({}, {responseType: 'blob'}))
            .then((response) => {
                const currentDate = CommonHelper.formatDate(new Date(), 'yyyyMMdd_HHmmss');

                fileDownload(response.data, `Liste Réserves ${currentDate}.csv`);
            });
    }

    static getLotsCsv(lotIds: number[]): Promise<void> {
        return axios.post(LOT_CSV_INDEX_URL,
            {
                ids: lotIds,
            },
            ApiHelper.buildHeaders({}, {responseType: 'blob'}))
            .then((response) => {
                const currentDate = CommonHelper.formatDate(new Date(), 'yyyyMMdd_HHmmss');

                fileDownload(response.data, `Liste Lots ${currentDate}.csv`);
            });
    }

    static postReserve(payload): Promise<AxiosResponse<Reserve>> {
        return axios.post(RESERVE_URL, payload, ApiHelper.buildHeaders());
    }

    static postReservePhoto(reserveId, photoFile: File): Promise<AxiosResponse<Photo>> {
        const formData = new FormData();
        formData.append('image', photoFile, photoFile.name);
        return axios.post(RESERVE_PHOTO_INDEX_URL.replace('{id}', reserveId), formData, ApiHelper.buildHeaders({
            'Content-Type': 'multipart/form-data'
        }));
    }

    static postReserveDocLevee(reserveId, photoFile: File): Promise<AxiosResponse<Photo>> {
        const formData = new FormData();
        formData.append('doc', photoFile, photoFile.name);
        return axios.post(RESERVE_DOC_LEVEE_INDEX_URL.replace('{id}', reserveId), formData, ApiHelper.buildHeaders({
            'Content-Type': 'multipart/form-data'
        }));
    }

    static patchReserve(reserveId, payload): Promise<AxiosResponse<Reserve>> {
        return axios.post(RESERVE_INDEX_URL.replace('{id}', reserveId), payload, ApiHelper.buildHeaders());
    }

    static updateReservesStatus(reserveIds: string[], statusId: number): Promise<AxiosResponse<Reserve[]>> {
        return axios.post(RESERVE_STATUS_INDEX_URL
                .replace('{id}', reserveIds.join(','))
                .replace('{idStatus}', String(statusId)),
            null,
            ApiHelper.buildHeaders()
        );
    }

    static getPhases() {
        return axios.get(PHASE_URL, ApiHelper.buildHeaders());
    }

    static getLocalisations() {
        return axios.get(LOCALISATION_URL, ApiHelper.buildHeaders());
    }

    static getSituations() {
        return axios.get(SITUATION_URL, ApiHelper.buildHeaders());
    }

    static getStatus() {
        return axios.get(STATUS_URL, ApiHelper.buildHeaders());
    }

    static getParameters() {
        return axios.get(PARAMETER_URL, ApiHelper.buildHeaders());
    }

    static getStaticUrl() {
        return STATIC_URL;
    }

    static getApiDocUrl() {
        return SERVER_URL + `/api-docs?token=${ApiHelper.getToken()}`;
    }
}
